import React from 'react';
import { compareAsc, parse } from 'date-fns';
import { withRouter } from '../../../../../../utils/withRouter';
import { Alert, Button, Col, CompositeTable, Container, NewModal as Modal, Row, Spinner, } from '@digitools/honest-react';
import useGet from '../../../../../../hooks/useGet';
import checkDate from 'status/utilities/checkDate';
import { absReasons, absTypes, enteredByTypes, submissionMethods, transTypes } from './UACodes';
import styles from './UnplannedAbsence.scss';
import { formatToUtc, formatEasternEpochAsLocal } from 'src/utils/dateUtils';
import { useDisclosure } from '@mantine/hooks';
import useTealium from '../../../../../../hooks/useTealium';
import { TEALIUM_EVENT } from '../../../../../../constants/tealiumConstants';
const formatDate = date => {
    if (date) {
        const formattedDate = checkDate(formatToUtc(date, 'MM/dd/yyyy'));
        if (formattedDate !== '') {
            return formattedDate;
        }
    }
    return 'N/A';
};
const formatDuration = duration => {
    if (duration && duration !== '00:00') {
        return duration;
    }
    return 'N/A';
};
const formatTime = time => {
    if (time) {
        if (checkDate(formatToUtc(time, 'MM/dd/yyyy')) !== '') {
            return formatToUtc(time, 'h:mm a');
        }
    }
    return 'N/A';
};
const formatSubmissionDate = date => {
    if (date) {
        const formattedDate = checkDate(formatEasternEpochAsLocal(date, 'MM/dd/yyyy'));
        if (formattedDate !== '') {
            return formattedDate;
        }
    }
    return 'N/A';
};
const formatSubmissionTime = time => {
    if (time) {
        if (checkDate(formatEasternEpochAsLocal(time, 'MM/dd/yyyy')) !== '') {
            return formatEasternEpochAsLocal(time, 'h:mm a');
        }
    }
    return 'N/A';
};
const formatFMLIndicator = fmlRelated => {
    if (fmlRelated === 'Y') {
        return 'Yes';
    }
    return 'No';
};
const OpenButton = ({ transNum, showModal, getUADetails }) => {
    const { trackEvent } = useTealium();
    const handleOnClick = () => {
        getUADetails();
        showModal();
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.BUTTON,
            event_name: TEALIUM_EVENT.EVENT_NAME.ABSENCE_ID,
            event_type: TEALIUM_EVENT.EVENT_TYPE.UNPLANNED_ABSENCE
        });
    };
    return (<Button data-testid='openmodal' style={{ paddingTop: '0', fontWeight: 'normal' }} onClick={handleOnClick} buttonType={'text'}>
      {transNum}
    </Button>);
};
const DisplayField = ({ label, children }) => (<Col xs={12} md={6} style={{ marginBottom: '8px' }}>
    <span style={{ fontWeight: 'bold' }}>{label}&nbsp;</span>
    {children}
  </Col>);
const UADetails = withRouter(({ getUADetails, uaDetails, error, loading, location }) => {
    const { trackEvent } = useTealium();
    const statusPath = location.pathname.split('/coveragedetail')[0];
    if (loading || (!uaDetails && !error)) {
        return (<div data-testid={'ua-details-loading'}>
        <Spinner id='uat-table-spinner1'/>
      </div>);
    }
    if (error) {
        return <Alert type={'error'}>There was a problem retrieving unplanned absence details.</Alert>;
    }
    let AssociatedTransField = () => <>N/A</>;
    if (uaDetails.associatedTransNum && uaDetails.associatedTransNum.trim()) {
        const getAssociatedTransNumDetails = () => {
            trackEvent({
                event_action: TEALIUM_EVENT.EVENT_ACTION.BUTTON,
                event_name: TEALIUM_EVENT.EVENT_NAME.ABSENCE_ID,
                event_type: TEALIUM_EVENT.EVENT_TYPE.UNPLANNED_ABSENCE,
                event_version: TEALIUM_EVENT.EVENT_VERSION.DETAILS,
            });
            getUADetails({ url: `/ua-service/absences/${uaDetails.associatedTransNum}/getAbsenceDetails` });
        };
        AssociatedTransField = () => (<Button style={{ paddingTop: '0', fontWeight: 'normal' }} onClick={getAssociatedTransNumDetails} buttonType={'text'}>
        {uaDetails.associatedTransNum}
      </Button>);
    }
    return (<Container>
      <div className='mb-4'>
        <DisplayField label='Event ID:'>{uaDetails.transNum}</DisplayField>
      </div>
        <Row>
          <DisplayField label='Transaction Number:'>{uaDetails.transNum}</DisplayField>
          <DisplayField label='Entered By:'>{enteredByTypes[uaDetails.enteredBy]}</DisplayField>
          <DisplayField label='Time Off Date:'>{formatDate(uaDetails.uaStartDate)}</DisplayField>
          <DisplayField label='Time Off Duration:'>{formatDuration(uaDetails.uaDuration)}</DisplayField>
          <DisplayField label='Time Off Type:'>{absTypes[uaDetails.absType]}</DisplayField>
          <DisplayField label='Time Off Reason:'>{absReasons[uaDetails.absReason]}</DisplayField>
          <DisplayField label='Start Time:'>{formatTime(uaDetails.uaStartTime)}</DisplayField>
          <DisplayField label='End Time:'>{formatTime(uaDetails.uaEndTime)}</DisplayField>
          <DisplayField label='Submission Date:'>
            {formatSubmissionDate(uaDetails.uaSubmitDate)} {formatSubmissionTime(uaDetails.uaSubmitTime)}
          </DisplayField>
          <DisplayField label='Submission Method:'>{submissionMethods[uaDetails.submissionMethod]}</DisplayField>
          <DisplayField label='Transaction Type:'>{transTypes[uaDetails.transType]}</DisplayField>
          <DisplayField label='Related Transaction Number:'>{<AssociatedTransField />}</DisplayField>
        </Row>
    </Container>);
});
const UAIDCell = ({ data }) => {
    const { error: uaDetailsError, loading: loadUaDetails, data: uaDetails, get: getUADetails, } = useGet({
        url: `/ua-service/absences/${data.transNum}/getAbsenceDetails`,
        noInitial: true,
        handleUnauthorized: true,
        options: {
            headers: {
                Accept: 'application/hal+json',
            },
        },
    });
    const [isOpen, { open, close }] = useDisclosure(false);
    return (<>
      <Modal data-testid='ua-details-modal' withCloseButton={true} title='Unplanned Absence Details' size='large' onClose={() => {
            close();
        }} opened={isOpen} cancelButtonText={'Close'} cancelButtonProps={{
            'data-testid': 'close-button',
            buttonType: 'primary',
            'aria-label': 'close unplanned absence'
        }} submitButton={false}>
        <UADetails getUADetails={getUADetails} uaDetails={uaDetails} error={uaDetailsError} loading={loadUaDetails}/>
      </Modal>
      <OpenButton transNum={data?.transNum} getUADetails={getUADetails} showModal={open}/>
    </>);
};
const UATable = ({ employeeData, empId }) => {
    const { loading, error, data: unplannedAbsences, } = useGet({
        url: '/ua-service/absences/getLastYear',
        options: {
            headers: {
                Accept: 'application/hal+json',
            },
            params: {
                empId: empId ? empId : employeeData.empId.trim(),
            },
        },
        handleUnauthorized: true,
    });
    if (loading) {
        return <Spinner id='uat-table-spinner2'/>;
    }
    if (error) {
        return <Alert type={'error'}>There was a problem retrieving unplanned absences.</Alert>;
    }
    if (!unplannedAbsences || unplannedAbsences.length === 0) {
        return <Alert type={'info'}>No unplanned absences found for this employee.</Alert>;
    }
    const data = [];
    if (unplannedAbsences.length > 0) {
        unplannedAbsences.forEach(ua => {
            const formattedUA = {
                transNum: ua.transNum,
                uaStartDate: formatDate(ua.uaStartDate),
                transType: transTypes[ua.transType],
                absReason: absReasons[ua.absReason],
            };
            data.push(formattedUA);
        });
    }
    const columns = [
        {
            Cell: UAIDCell,
            Header: 'Transaction #',
            accessor: 'transNum',
            id: 'transNum',
        },
        {
            Header: 'Date',
            accessor: 'uaStartDate',
            id: 'uaStartDate',
            sortMethod: (a, b) => compareAsc(parse(a, 'P', new Date()), parse(b, 'P', new Date())),
            Cell: ({ data }) => {
                return <span data-private={true}>{data.uaStartDate}</span>;
            },
        },
        {
            Header: 'Type',
            accessor: 'transType',
            id: 'transType',
        },
        {
            Header: 'Reason',
            accessor: 'absReason',
            id: 'absReason',
            Cell: ({ data }) => {
                return <span data-private={true}>{data.absReason}</span>;
            },
        },
    ];
    const defaultSorted = [
        {
            id: 'uaStartDate',
            desc: true,
        },
    ];
    return (<div className={styles.tableContainer}>
      <CompositeTable data-testid='ua-table' data={data} columns={columns} defaultSorted={defaultSorted} paginated={data.length > 10}/>
    </div>);
};
export default UATable;
